import { extractImageFrames, clampTo, hasVideoInput } from '../../tools';
import type { baiduModelConfig } from '../types';


export const klingConfigs: Record<string, baiduModelConfig> = {
    'kling-v3.0': {
        // https://tcnxnzs113h4.feishu.cn/wiki/SB8fwteTyiAKjSk3StgcI7OWn0b
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/v3/aigc/kl/videos/image2video';
            } else {
                return '/v3/aigc/kl/videos/text2video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model_name: 'kling-v3',
                    prompt: prompt.slice(0, 2500),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                    aspect_ratio: undefined,
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 首帧 URL
                    image: firstImage,
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    cfg_scale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    voice_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model_name: 'kling-v3',
                    prompt: prompt.slice(0, 2500),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 首帧 URL
                    image: firstImage,
                    // 尾帧 URL
                    image_tail: lastImage,
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    cfg_scale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    voice_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            // 文生视频模式
            } else {
                return {
                    model_name: 'kling-v3',
                    prompt: prompt.slice(0, 2500),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    cfg_scale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    watermark_info: { 'enabled': false },
                };
            }
        },
    },
    'kling-v3.0-omni': {
        // https://tcnxnzs113h4.feishu.cn/wiki/SB8fwteTyiAKjSk3StgcI7OWn0b
        apiPath: '/v3/aigc/kl/videos/omni-video',
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model_name: 'kling-v3-omni',
                    prompt: prompt.slice(0, 2500),
                    aspect_ratio: undefined,
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 首帧 URL
                    image_list: [
                        { 
                            image_url: firstImage, 
                            type: "first_frame",
                        },
                    ],
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    video_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model_name: 'kling-v3-omni',
                    prompt: prompt.slice(0, 2500),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 首尾帧 URL
                    image_list: [
                        { 
                            image_url: firstImage, 
                            type: "first_frame",
                        },
                        { 
                            image_url: lastImage, 
                            type: "end_frame",
                        },
                    ],
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    video_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model_name: 'kling-v3-omni',
                    prompt: prompt.slice(0, 2500),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 参考帧 URL
                    image_list: (imagePrompt ?? []).filter(Boolean).slice(0, 7).map((url) => ({ image_url: url })),
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    video_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            // 文生视频模式
            } else {
                return {
                    model_name: 'kling-v3-omni',
                    prompt: prompt.slice(0, 2500),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    image_list: undefined,
                    // 可选参数
                    sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    element_list: undefined,
                    video_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            }
        },
    },
    'kling-v3.0-omni-v2v': {
        // https://tcnxnzs113h4.feishu.cn/wiki/SB8fwteTyiAKjSk3StgcI7OWn0b
        apiPath: '/v3/aigc/kl/videos/omni-video',
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, videoUri, aspectRatio, quality } = params;
            const hasVideo = hasVideoInput(videoUri);
            if (hasVideo) {
                return {
                    model_name: 'kling-v3-omni',
                    prompt: prompt.slice(0, 2500),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: undefined,
                    mode: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '720p' ? 'std' : clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p') === '1080p' ? 'pro' : '4k',
                    // 视频 URL
                    video_list: [ 
                        { 
                            video_url: videoUri,
                            refer_type: 'base', 
                            keep_original_sound: 'yes',
                        }
                    ],
                    // 可选参数
                    sound: 'off',
                    // system
                    multi_shot: false,
                    shot_type: undefined,
                    multi_prompt: undefined,
                    image_list: undefined,
                    element_list: undefined,
                    watermark_info: { 'enabled': false },
                };
            } else {
                throw new Error('Missing Video Input');
            }
        },
    },
    'kling-v2.6': {
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#texttovideok26taskinput
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#imagetovideok26taskinput
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/v3/aigc/image_to_video';
            } else {
                return '/v3/aigc/text_to_video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'K26',
                    modelK26TaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 首帧 URL
                        image: firstImage,
                        // 可选参数
                        sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: 'pro',
                        voice: 'off',
                        voiceList: undefined,
                    }
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'K26',
                    modelK26TaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 首尾帧
                        headtailImages: {
                            headImage: firstImage,
                            tailImage: lastImage || firstImage,
                        },
                        // 可选参数
                        sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: 'pro',
                        voice: 'off',
                        voiceList: undefined,
                    }
                };
            // 文生视频模式
            } else {
                return {
                    model: 'K26',
                    modelK26TaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 可选参数
                        sound: typeof providerOptions?.sound === 'boolean' ? providerOptions.sound === true ? 'on' : 'off' : 'on',
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: 'pro',
                    }
                };
            }
        },
    },
    'kling-v2.5': {
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#texttovideok25ttaskinput
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#imagetovideok25ttaskinput
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/v3/aigc/image_to_video';
            } else {
                return '/v3/aigc/text_to_video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'K25T',
                    modelK25TTaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 首帧 URL
                        image: firstImage,
                        // 可选参数
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p') === '1080p' ? 'pro' : 'std',
                    }
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'K25T',
                    modelK25TTaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 首尾帧
                        headtailImages: {
                            headImage: firstImage,
                            tailImage: lastImage || firstImage,
                        },
                        // 可选参数
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: 'pro',
                    }
                };
            // 文生视频
            } else {
                return {
                    model: 'K25T',
                    modelK25TTaskInput:{
                        prompt: prompt.slice(0, 2500),
                        negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2500) : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                        duration: clampTo(duration || 5, [5, 10] as const, 5),
                        // 可选参数
                        cfgScale: typeof providerOptions?.cfg_scale === 'number' ? providerOptions.cfg_scale : undefined,
                        // system
                        mode: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p') === '1080p' ? 'pro' : 'std',
                    }
                };
            }
        },
    },
    'kling-v2-avatar': {
        apiPath: '/v3/aigc/kl/videos/avatar/image2video',
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, audioUri, quality } = params;
            const [firstImage] = extractImageFrames(imagePrompt);
            return {
                prompt: prompt?.slice(0, 2500) || undefined,
                mode: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p') === '1080p' ? 'pro' : 'std',
                // 首帧 URL
                image: firstImage,
                // 音频 URL
                sound_file: audioUri,
                // 可选参数
                watermark_info: { enabled: false },
            };
        },
    },
};